import { Injectable, signal, computed, inject } from '@angular/core';
import {
  Post, FunnelState, CarouselSlide, ProposedWeekPost, ContentPillar
} from '../models/post.model';
import { SupabaseService } from './supabase.service';
import { AuthService } from './auth.service';

const INITIAL_FUNNEL: FunnelState = {
  step: 0,
  platform: 'linkedin',
  format: 'li',
  style: 'mono',
  template: 'headline',
  language: 'de',
  content_pillar: '',
  topic_key: '',
  freetext: '',
  generating: false,
  slides: [],
  generatingSlides: false,
  generatingImage: false,
  reel_composition: 'cyan',
  generatingReel: false,
};

@Injectable({ providedIn: 'root' })
export class PostService {
  private readonly supa = inject(SupabaseService);
  private readonly auth = inject(AuthService);

  private _posts   = signal<Post[]>([]);
  private _loading = signal(true);
  private _funnel  = signal<FunnelState>(INITIAL_FUNNEL);

  constructor() {
    this.auth.waitForInit().then(() => {
      if (this.auth.isAuthenticated()) this._loadFromSupabase();
      else this._loading.set(false);
    });
  }

  private async _loadFromSupabase(): Promise<void> {
    this._loading.set(true);
    const posts = await this.supa.loadPosts();
    this._posts.set(posts);
    this._loading.set(false);
  }

  readonly posts   = this._posts.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly funnel  = this._funnel.asReadonly();

  readonly draftCount    = computed(() => this._posts().filter(p => p.status === 'draft').length);
  readonly approvedCount = computed(() => this._posts().filter(p => p.status === 'approved').length);
  readonly totalCount    = computed(() => this._posts().length);

  getById(id: string): Post | undefined {
    return this._posts().find(p => p.id === id);
  }

  addPost(post: Post): void {
    this._posts.update(ps => [post, ...ps]);
    this.supa.upsertPost(post);
  }

  updatePost(id: string, changes: Partial<Post>): void {
    this._posts.update(ps => ps.map(p => p.id === id ? { ...p, ...changes } : p));
    const updated = this._posts().find(p => p.id === id);
    if (updated) this.supa.upsertPost(updated);
  }

  approvePost(id: string): void { this.updatePost(id, { status: 'approved' }); }

  deletePost(id: string): void {
    this._posts.update(ps => ps.filter(p => p.id !== id));
    this.supa.deletePost(id);
  }

  setFunnelField<K extends keyof FunnelState>(key: K, value: FunnelState[K]): void {
    this._funnel.update(f => ({ ...f, [key]: value }));
  }

  funnelNext(): void { this._funnel.update(f => ({ ...f, step: Math.min(5, f.step + 1) })); }
  funnelBack(): void { this._funnel.update(f => ({ ...f, step: Math.max(0, f.step - 1) })); }
  resetFunnel(): void { this._funnel.set({ ...INITIAL_FUNNEL }); }

  setGenerating(val: boolean): void     { this._funnel.update(f => ({ ...f, generating: val })); }
  setGenerated(content: FunnelState['generated']): void {
    this._funnel.update(f => ({ ...f, generated: content, generating: false, step: 3 }));
  }
  setGeneratingSlides(val: boolean): void { this._funnel.update(f => ({ ...f, generatingSlides: val })); }
  setSlides(slides: CarouselSlide[]): void {
    this._funnel.update(f => ({ ...f, slides, generatingSlides: false }));
  }
  setGeneratingImage(val: boolean): void { this._funnel.update(f => ({ ...f, generatingImage: val })); }
  setImageUrl(url: string): void {
    this._funnel.update(f => ({ ...f, imageUrl: url, generatingImage: false }));
  }

  addWeekPlanPosts(proposals: ProposedWeekPost[]): void {
    const now = new Date().toISOString();
    const newPosts: Post[] = proposals.map((p, i) => ({
      id: 'wp' + Date.now() + i,
      post_key: this.generatePostKey(),
      status: 'draft' as const,
      platform: p.platform,
      format: p.platform === 'instagram' ? 'ig' as const : 'li' as const,
      style: 'mono' as const,
      template: 'headline' as const,
      content_pillar: p.content_pillar,
      topic_key: p.topic_key,
      week_tag: `KW ${this._currentWeek()} · ${new Date().getFullYear()}`,
      pillar_label: p.content_pillar,
      headline_line_1: p.headline_line_1,
      highlight_word: p.highlight_word,
      subline: p.subline,
      hook: p.hook,
      caption: p.caption,
      hashtags: p.hashtags,
      human_edited: false,
      created_at: now,
    }));
    this._posts.update(ps => [...newPosts, ...ps]);
    newPosts.forEach(p => this.supa.upsertPost(p));
  }

  exportPlaywrightJson(): object[] {
    return this._posts().map(p => ({
      post_key:        p.post_key,
      platform:        p.platform,
      format:          p.format,
      style:           p.style,
      template:        p.template ?? 'headline',
      pillar_label:    p.pillar_label,
      week_tag:        p.week_tag,
      headline_line_1: p.headline_line_1,
      highlight_word:  p.highlight_word ?? null,
      headline_line_2: p.headline_line_2 ?? null,
      subline:         p.subline ?? null,
      stat_value:      p.stat_value ?? null,
      stat_label:      p.stat_label ?? null,
      stat_context:    p.stat_context ?? null,
      quote_text:      p.quote_text ?? null,
      quote_source:    p.quote_source ?? null,
      list_items:      p.list_items ?? null,
      imageUrl:        p.imageUrl ?? null,
      slides:          p.slides ?? [],
    }));
  }

  generatePostKey(): string {
    const now  = new Date();
    const week = this._currentWeek();
    const n    = (this._posts().length + 1).toString().padStart(3, '0');
    return `sw-${now.getFullYear()}-${week.toString().padStart(2, '0')}-${n}`;
  }

  private _currentWeek(): number {
    const now = new Date();
    return Math.ceil(((now.getTime() - new Date(now.getFullYear(), 0, 1).getTime()) / 86400000 + 1) / 7);
  }
}
